import { useEffect, useState } from 'react';
import { createHashRouter, RouterProvider, Navigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Layout } from './Layout';
import { ErrorScreen, RouteErrorScreen } from './ErrorScreen';
import { TodayPage } from '@/features/today/TodayPage';
import { MorePage } from '@/features/more/MorePage';
import { ProfilePage } from '@/features/more/ProfilePage';
import { SettingsPage } from '@/features/more/SettingsPage';
import { AboutPage } from '@/features/more/AboutPage';
import { ExcelPage } from '@/features/more/ExcelPage';
import { OnboardingPage } from '@/features/onboarding/OnboardingPage';
import { InstallHelpPage } from '@/features/pwa/InstallHelpPage';
import { PlaceholderPage } from '@/features/placeholder/PlaceholderPage';
import { BackupPage } from '@/features/backup/BackupPage';
import { CopiesPage, EncryptionPage, RestorePage } from '@/features/backup/PaginasRespaldo';
import { GymPage } from '@/features/gym/GymPage';
import { PlantillaPage } from '@/features/gym/PlantillaPage';
import { DescansosPage } from '@/features/gym/DescansosPage';
import { RutinaPage } from '@/features/gym/RutinaPage';
import { EjercicioPage, EjerciciosPage } from '@/features/gym/EjerciciosPages';
import { EntrenamientoPage } from '@/features/gym/EntrenamientoPage';
import { AgregarAEntrenamientoPage } from '@/features/gym/AgregarAEntrenamientoPage';
import { ResumenPage } from '@/features/gym/ResumenPage';
import { DiscosPage } from '@/features/gym/DiscosPage';
import { ImportarRutinaPage } from '@/features/gym/ImportarRutinaPage';
import { EntrenamientoHistorialPage, HistorialPage } from '@/features/gym/HistorialPages';
import { FoodPage } from '@/features/food/FoodPage';
import { AgregarPage } from '@/features/food/AgregarPage';
import { CopiarTiempoPage } from '@/features/food/CopiarPage';
import { EditarRegistroPage, RapidoPage, RegistrarPage } from '@/features/food/RegistroPages';
import { AlimentoPage, MisAlimentosPage } from '@/features/food/MisAlimentosPages';
import { CodigoDeBarrasPage } from '@/features/food/CodigoDeBarrasPage';
import { PlatilloPage, PlatillosPage } from '@/features/food/PlatillosPages';
import { ConfirmarHabitualPage, HabitualesPage } from '@/features/food/HabitualesPages';
import { AyunosPage } from '@/features/food/AyunoPages';
import { FinDeEtapaPage, MetasEtapaPage, NuevoPlanPage, PlanPage } from '@/features/plan/PlanPages';
import { MantenimientoPage, MetasManualesPage } from '@/features/plan/MetasPages';
import { BodyPage } from '@/features/body/BodyPage';
import { BasculaPage, BasculasPage, EstaturaPage, PesajePage, PesajesPage } from '@/features/body/PesoPages';
import { CapturaMedidasPage, MedidaPage, MedidaTipoPage, MedidasPage } from '@/features/body/MedidasPages';
import { FotoNuevaPage, FotoPage, FotosPage } from '@/features/body/FotosPages';
import { db, asegurarSettings, actualizarSettings, leerSettings } from '@/db/schema';
import { copiarAntesDeActualizar } from '@/db/proteccion';
import { asegurarCatalogoBase } from '@/db/ejercicios';
import { solicitarPersistencia } from '@/lib/storage';
import { establecerTema } from '@/lib/tema';

/**
 * Enrutado por fragmento (#): en un hosting estático como Cloudflare Pages,
 * recargar una ruta profunda no da 404, porque el navegador nunca pide esa
 * ruta al servidor. En la app instalada la barra de direcciones no se ve.
 */
const router = createHashRouter([
  {
    // Fuera del Layout: pantalla completa, sin la barra de pestañas (Req. 4.1).
    path: '/primeros-pasos',
    element: <OnboardingPage />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: '/',
    element: <Layout />,
    // Cualquier fallo dentro de la app ofrece una salida (Req. 3.1).
    errorElement: <RouteErrorScreen />,
    children: [
      { index: true, element: <TodayPage /> },
      { path: 'gym', element: <GymPage /> },
      { path: 'gym/plantilla', element: <PlantillaPage /> },
      { path: 'gym/descansos', element: <DescansosPage /> },
      { path: 'gym/rutina/:id', element: <RutinaPage /> },
      { path: 'gym/ejercicios', element: <EjerciciosPage /> },
      { path: 'gym/ejercicios/nuevo', element: <EjercicioPage /> },
      { path: 'gym/ejercicios/:id', element: <EjercicioPage /> },
      { path: 'gym/entrenamiento', element: <EntrenamientoPage /> },
      { path: 'gym/entrenamiento/agregar/:exerciseId', element: <AgregarAEntrenamientoPage /> },
      { path: 'gym/entrenamiento/:id/resumen', element: <ResumenPage /> },
      { path: 'gym/discos', element: <DiscosPage /> },
      { path: 'gym/importar-rutina', element: <ImportarRutinaPage /> },
      { path: 'gym/historial', element: <HistorialPage /> },
      { path: 'gym/historial/:id', element: <EntrenamientoHistorialPage /> },
      { path: 'comida', element: <FoodPage /> },
      { path: 'comida/agregar', element: <AgregarPage /> },
      { path: 'comida/agregar/rapido', element: <RapidoPage /> },
      { path: 'comida/copiar', element: <CopiarTiempoPage /> },
      { path: 'comida/agregar/:origen/:id', element: <RegistrarPage /> },
      { path: 'comida/registro/:id', element: <EditarRegistroPage /> },
      { path: 'comida/mis-alimentos', element: <MisAlimentosPage /> },
      { path: 'comida/codigo', element: <CodigoDeBarrasPage /> },
      { path: 'comida/mis-alimentos/nuevo', element: <AlimentoPage /> },
      { path: 'comida/mis-alimentos/:id', element: <AlimentoPage /> },
      { path: 'comida/platillos', element: <PlatillosPage /> },
      { path: 'comida/platillos/nuevo', element: <PlatilloPage /> },
      { path: 'comida/platillos/:id', element: <PlatilloPage /> },
      { path: 'comida/habituales', element: <HabitualesPage /> },
      { path: 'comida/habitual/:id', element: <ConfirmarHabitualPage /> },
      { path: 'comida/ayunos', element: <AyunosPage /> },
      { path: 'cuerpo', element: <BodyPage /> },
      { path: 'cuerpo/pesaje/nuevo', element: <PesajePage /> },
      { path: 'cuerpo/pesaje/:id', element: <PesajePage /> },
      { path: 'cuerpo/pesajes', element: <PesajesPage /> },
      { path: 'cuerpo/basculas', element: <BasculasPage /> },
      { path: 'cuerpo/basculas/nueva', element: <BasculaPage /> },
      { path: 'cuerpo/basculas/:id', element: <BasculaPage /> },
      { path: 'cuerpo/estatura', element: <EstaturaPage /> },
      { path: 'cuerpo/medidas', element: <MedidasPage /> },
      { path: 'cuerpo/medidas/nuevas', element: <CapturaMedidasPage /> },
      { path: 'cuerpo/medidas/:tipo', element: <MedidaTipoPage /> },
      { path: 'cuerpo/medida/:id', element: <MedidaPage /> },
      { path: 'cuerpo/fotos', element: <FotosPage /> },
      { path: 'cuerpo/fotos/nueva', element: <FotoNuevaPage /> },
      { path: 'cuerpo/foto/:id', element: <FotoPage /> },
      { path: 'mas', element: <MorePage /> },
      { path: 'mas/perfil', element: <ProfilePage /> },
      { path: 'mas/respaldos', element: <BackupPage /> },
      { path: 'mas/respaldos/restaurar', element: <RestorePage /> },
      { path: 'mas/respaldos/cifrado', element: <EncryptionPage /> },
      { path: 'mas/respaldos/copias', element: <CopiesPage /> },
      { path: 'mas/ajustes', element: <SettingsPage /> },
      { path: 'mas/excel', element: <ExcelPage /> },
      { path: 'mas/acerca', element: <AboutPage /> },
      { path: 'mas/instalar', element: <InstallHelpPage /> },
      { path: 'mas/plan', element: <PlanPage /> },
      { path: 'mas/plan/nuevo', element: <NuevoPlanPage /> },
      { path: 'mas/plan/metas', element: <MetasEtapaPage /> },
      { path: 'mas/plan/fin', element: <FinDeEtapaPage /> },
      { path: 'mas/mantenimiento', element: <MantenimientoPage /> },
      { path: 'mas/metas', element: <MetasManualesPage /> },
      { path: 'mas/excel', element: <PlaceholderPage ruta="/mas/excel" /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

type EstadoArranque = { fase: 'cargando' } | { fase: 'listo' } | { fase: 'error'; error: unknown };

/** Mantiene el tema aplicado si cambia en los ajustes, por ejemplo al restaurar. */
function SincronizarTema() {
  const tema = useLiveQuery(async () => (await leerSettings()).tema, []);
  useEffect(() => {
    if (tema !== undefined) establecerTema(tema);
  }, [tema]);
  return null;
}

export function App() {
  const [estado, setEstado] = useState<EstadoArranque>({ fase: 'cargando' });

  /**
   * Arranque (tareas 4.2 y 6.5):
   * 1. Si una versión nueva trae un esquema nuevo, guarda una copia interna de
   *    los datos ANTES de que Dexie los migre (Req. 2.10). Si la copia o la
   *    migración fallan, los datos quedan como estaban y se muestra la
   *    pantalla de recuperación, que ofrece descargar la copia.
   * 2. Crea los ajustes y el perfil si no existen y aplica el tema.
   * 3. Pide almacenamiento persistente (Req. 1.6).
   * Es idempotente: el doble montaje de StrictMode en desarrollo no causa problema.
   */
  useEffect(() => {
    let vigente = true;

    void (async () => {
      try {
        // db.verno es la versión más alta declarada en schema.ts, aunque la base
        // todavía no esté abierta: no depende de una constante escrita a mano.
        await copiarAntesDeActualizar(db.name, db.verno);
        const settings = await asegurarSettings();
        // Los ejercicios base que falten (Req. 9.1, 9.7); nunca toca los existentes
        await asegurarCatalogoBase();
        establecerTema(settings.tema);
        if (vigente) setEstado({ fase: 'listo' });

        // No bloquea el arranque: un rechazo no es un error (Req. 1.6).
        const persistente = await solicitarPersistencia();
        await actualizarSettings({ almacenamientoPersistente: persistente });
      } catch (error) {
        if (vigente) setEstado({ fase: 'error', error });
      }
    })();

    return () => {
      vigente = false;
    };
  }, []);

  // Si la base de datos no abre, la misma pantalla de recuperación ofrece una
  // salida que no implique borrar la app.
  if (estado.fase === 'error') return <ErrorScreen error={estado.error} />;
  if (estado.fase === 'cargando') {
    return <div className="min-h-dvh bg-fondo" aria-busy="true" aria-label="Cargando DuoFit" />;
  }

  return (
    <>
      <SincronizarTema />
      <RouterProvider router={router} />
    </>
  );
}
